import { useEffect, useState } from 'react';
import { isApiError } from '../../lib/api';
import { fetchRouting, saveRouting, type RoutingSetup } from '../../lib/routing-api';
import styles from './admin.module.css';

// Departments, routing rules and assignment for one site. Rules run in order
// on a conversation's first message; the first whose conditions all match
// sends it to its department. With automatic assignment, a chat waiting for a
// human then goes to the least-busy available member of that department.

const CHANNELS = [
  { id: 'web', label: 'Website' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'messenger', label: 'Messenger' },
  { id: 'api', label: 'API' },
] as const;

interface DeptDraft {
  key: string;
  id?: string;
  name: string;
  agentIds: string[];
}

interface RuleDraft {
  key: string;
  name: string;
  department: string;
  pageContains: string;
  countries: string;
  channels: string[];
  keywords: string;
}

let nextKey = 0;
const newKey = () => `k${++nextKey}`;
const splitList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

function toDrafts(setup: RoutingSetup): { depts: DeptDraft[]; rules: RuleDraft[] } {
  return {
    depts: setup.departments.map((d) => ({ key: newKey(), id: d.id, name: d.name, agentIds: d.agentIds })),
    rules: setup.rules.map((r) => ({
      key: newKey(),
      name: r.name,
      department: r.department,
      pageContains: r.conditions.pageContains ?? '',
      countries: (r.conditions.countries ?? []).join(', '),
      channels: r.conditions.channels ?? [],
      keywords: (r.conditions.keywords ?? []).join(', '),
    })),
  };
}

const ERRORS: Record<string, string> = {
  'duplicate-department': 'Two departments have the same name.',
  'unknown-department': 'A rule sends chats to a department that no longer exists.',
  'invalid-conditions': 'A rule has a condition that isn’t valid.',
  'agent-not-on-site': 'A department member is no longer on this site.',
};

export function RoutingSection({ siteId }: { siteId: string }) {
  const [setup, setSetup] = useState<RoutingSetup | null>(null);
  const [mode, setMode] = useState<'manual' | 'auto'>('manual');
  const [depts, setDepts] = useState<DeptDraft[]>([]);
  const [rules, setRules] = useState<RuleDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function apply(s: RoutingSetup) {
    setSetup(s);
    setMode(s.assignmentMode);
    const d = toDrafts(s);
    setDepts(d.depts);
    setRules(d.rules);
  }

  useEffect(() => {
    fetchRouting(siteId)
      .then(apply)
      .catch(() => setError('Couldn’t load routing for this site.'));
  }, [siteId]);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const result = await saveRouting(siteId, {
        assignmentMode: mode,
        departments: depts.map((d) => ({ ...(d.id ? { id: d.id } : {}), name: d.name.trim(), agentIds: d.agentIds })),
        rules: rules.map((r) => ({
          name: r.name.trim() || 'Rule',
          department: r.department,
          conditions: {
            ...(r.pageContains.trim() ? { pageContains: r.pageContains.trim() } : {}),
            ...(splitList(r.countries).length ? { countries: splitList(r.countries) } : {}),
            ...(r.channels.length ? { channels: r.channels } : {}),
            ...(splitList(r.keywords).length ? { keywords: splitList(r.keywords) } : {}),
          },
        })),
      });
      apply(result);
      setSaved(true);
    } catch (err) {
      const code = isApiError(err) ? (err.body as { error?: string } | undefined)?.error : undefined;
      setError((code && ERRORS[code]) ?? 'Couldn’t save routing. Check every department has a name and every rule a department.');
    } finally {
      setBusy(false);
    }
  }

  if (!setup) return <section className={styles.section}>{error ? <p className={styles.error} role="alert">{error}</p> : <p className={styles.hint}>Loading…</p>}</section>;

  const patchDept = (key: string, p: Partial<DeptDraft>) => setDepts((ds) => ds.map((d) => (d.key === key ? { ...d, ...p } : d)));
  const patchRule = (key: string, p: Partial<RuleDraft>) => setRules((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const moveRule = (i: number, by: number) =>
    setRules((rs) => {
      const next = [...rs];
      const [r] = next.splice(i, 1);
      next.splice(Math.max(0, Math.min(next.length, i + by)), 0, r!);
      return next;
    });
  const deptNames = depts.map((d) => d.name.trim()).filter(Boolean);

  return (
    <>
      <section className={styles.section}>
        <p className={styles.kicker}>Routing</p>
        <h2 className={styles.title}>Who gets a new chat</h2>
        <label className={styles.field}>
          <span className={styles.label}>Assignment</span>
          <select className={styles.select} value={mode} onChange={(e) => setMode(e.target.value as 'manual' | 'auto')}>
            <option value="manual">Manual — agents pick chats up (their department is notified)</option>
            <option value="auto">Automatic — give each waiting chat to the least-busy available agent</option>
          </select>
          <span className={styles.hint}>
            Automatic assignment prefers members of the chat’s department, then anyone on the site who is
            available; ties go to whoever was assigned longest ago. A chat the AI is handling is assigned when
            it hands over to a person.
          </span>
        </label>
      </section>

      <section className={styles.section}>
        <h2 className={styles.title}>Departments</h2>
        {depts.length === 0 ? <p className={styles.hint}>No departments yet. Add one, e.g. Sales or Billing.</p> : null}
        {depts.map((d) => (
          <div key={d.key} className={styles.form} style={{ marginBottom: 'var(--inic-space-3)' }} aria-label={`Department ${d.name || 'new'}`}>
            <label className={styles.field}>
              <span className={styles.label}>Name</span>
              <input className={styles.input} value={d.name} maxLength={60} onChange={(e) => patchDept(d.key, { name: e.target.value })} placeholder="Sales" />
            </label>
            <div className={styles.field}>
              <span className={styles.label}>Members</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--inic-space-3)' }}>
                {setup.agents.map((a) => (
                  <label key={a.id} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={d.agentIds.includes(a.id)}
                      onChange={(e) =>
                        patchDept(d.key, { agentIds: e.target.checked ? [...d.agentIds, a.id] : d.agentIds.filter((x) => x !== a.id) })
                      }
                    />
                    {a.name}
                  </label>
                ))}
                {setup.agents.length === 0 ? <span className={styles.hint}>No agents on this site yet.</span> : null}
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="button" className={styles.buttonDanger} onClick={() => setDepts((ds) => ds.filter((x) => x.key !== d.key))}>
                Remove department
              </button>
            </div>
          </div>
        ))}
        <button type="button" className={styles.buttonGhost} onClick={() => setDepts((ds) => [...ds, { key: newKey(), name: '', agentIds: [] }])}>
          + Add department
        </button>
      </section>

      <section className={styles.section}>
        <h2 className={styles.title}>Rules</h2>
        <p className={styles.hint}>
          Checked in order on a conversation’s first message; the first rule whose conditions all match wins. A
          rule with no conditions matches everything — keep it last as the catch-all.
        </p>
        {rules.map((r, i) => (
          <div key={r.key} className={styles.form} style={{ marginBottom: 'var(--inic-space-3)' }} aria-label={`Rule ${i + 1}`}>
            <label className={styles.field}>
              <span className={styles.label}>Rule {i + 1} name</span>
              <input className={styles.input} value={r.name} maxLength={80} onChange={(e) => patchRule(r.key, { name: e.target.value })} placeholder="Refunds" />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Send to department</span>
              <select className={styles.select} value={r.department} onChange={(e) => patchRule(r.key, { department: e.target.value })}>
                <option value="">Choose…</option>
                {deptNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Page URL contains</span>
              <input className={styles.input} value={r.pageContains} onChange={(e) => patchRule(r.key, { pageContains: e.target.value })} placeholder="/pricing" />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>First message mentions (comma-separated)</span>
              <input className={styles.input} value={r.keywords} onChange={(e) => patchRule(r.key, { keywords: e.target.value })} placeholder="refund, invoice, billing" />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Visitor country (comma-separated)</span>
              <input className={styles.input} value={r.countries} onChange={(e) => patchRule(r.key, { countries: e.target.value })} placeholder="India, Nepal" />
            </label>
            <div className={styles.field}>
              <span className={styles.label}>Channel</span>
              <div style={{ display: 'flex', gap: 'var(--inic-space-3)' }}>
                {CHANNELS.map((c) => (
                  <label key={c.id} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={r.channels.includes(c.id)}
                      onChange={(e) =>
                        patchRule(r.key, { channels: e.target.checked ? [...r.channels, c.id] : r.channels.filter((x) => x !== c.id) })
                      }
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="button" className={styles.buttonGhost} disabled={i === 0} onClick={() => moveRule(i, -1)}>↑ Up</button>
              <button type="button" className={styles.buttonGhost} disabled={i === rules.length - 1} onClick={() => moveRule(i, 1)}>↓ Down</button>
              <button type="button" className={styles.buttonDanger} onClick={() => setRules((rs) => rs.filter((x) => x.key !== r.key))}>Remove rule</button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className={styles.buttonGhost}
          disabled={deptNames.length === 0}
          title={deptNames.length === 0 ? 'Add a department first' : undefined}
          onClick={() => setRules((rs) => [...rs, { key: newKey(), name: '', department: deptNames[0] ?? '', pageContains: '', countries: '', channels: [], keywords: '' }])}
        >
          + Add rule
        </button>
      </section>

      <section className={styles.section}>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {saved ? <p className={styles.hint}>Saved.</p> : null}
        <button type="button" className={styles.button} disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save routing'}
        </button>
      </section>
    </>
  );
}
